import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import ReactApexChart from 'react-apexcharts';
import 'react-datepicker/dist/react-datepicker.css';
import { useSelector } from 'react-redux';
import { getCurrency } from '../services/tableHelper';

const ChartContainer = styled.div`
	background-color: white;
	padding: 30px;
	//   border-radius: 8px;
	margin-top: 20px;
	//   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
	border-bottom: 1px solid #eaeaea;

	@media (max-width: 768px) {
		padding: 20px 15px;
	}
`;

const ChartHeader = styled.div`
	display: flex;
	flex-direction: column;
	margin-bottom: 30px;
`;

const Title = styled.h2`
	margin: 0;
	color: #3e4c59;
	font-weight: 600;
	font-size: 24px;
`;

const Subtitle = styled.p`
	margin: 5px 0 30px 0;
	color: #8795a1;
	font-size: 16px;
`;

const MetricsContainer = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 20px;
	margin-bottom: 30px;

	@media (max-width: 768px) {
		flex-direction: column;
	}
`;

const MetricBox = styled.div`
	flex: 1;
	min-width: 200px;
`;

const MetricLabel = styled.div`
	font-size: 16px;
	color: ${(props) => props.color || '#3e4c59'};
	margin-bottom: 5px;
`;

const MetricValue = styled.div`
	font-size: 32px;
	font-weight: 600;
	color: #2d3748;
	display: flex;
	align-items: baseline;
`;

const CurrencyPrefix = styled.span`
	font-size: 20px;
	color: #8795a1;
	margin-right: 5px;
`;

// Data structure for sales data
export interface SalesDataItem {
	date: string;
	total_amount_sold: string;
}

type Period = 'This week' | 'This month' | 'This year' | 'Custom Range';

interface SalesChartProps {
	initialStartDate?: Date;
	initialEndDate?: Date;
	salesData?: SalesDataItem[];
	selectedPeriod?: Period;
}

// Type definitions for ApexCharts tooltip parameters
interface TooltipContext {
	dataPointIndex: number;
	seriesIndex: number;
	w: {
		globals: {
			categoryLabels: string[];
		};
	};
}

interface CustomTooltipContext {
	series: number[][];
	seriesIndex: number;
	dataPointIndex: number;
	w: {
		globals: {
			categoryLabels: string[];
		};
	};
}

const SalesChart: React.FC<SalesChartProps> = ({
	initialStartDate = new Date(new Date().setMonth(new Date().getMonth() - 1)),
	initialEndDate = new Date(),
	salesData = [],
	selectedPeriod = 'This month',
}) => {
	const [startDate, setStartDate] = useState<Date>(initialStartDate);
	const [endDate, setEndDate] = useState<Date>(initialEndDate);
	const [filteredSalesData, setFilteredSalesData] = useState<SalesDataItem[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [aggregatedData, setAggregatedData] = useState<{ date: string; value: number }[]>([]);
	const [categories, setCategories] = useState<string[]>([]);

	const currency = useSelector((state: any) => getCurrency(state.auth.user.currency));

	// Function to format date based on period
	const formatDateForPeriod = (date: Date): string => {
		switch (selectedPeriod) {
			case 'This week':
				// Return day of week (Monday, Tuesday, etc.)
				return date.toLocaleString('default', { weekday: 'long' });
			case 'This year':
				// Return month name (January, February, etc.)
				return date.toLocaleString('default', { month: 'long' });
			default:
				// For month and custom range, return day with month
				return `${date.getDate()}th ${date.toLocaleString('default', { month: 'short' })}`;
		}
	};

	// Function to determine the best aggregation type based on date range
	const determineAggregationType = (start: Date, end: Date): 'day' | 'month' | 'year' => {
		const diffTime = Math.abs(end.getTime() - start.getTime());
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

		if (diffDays <= 30) {
			// 30 days or less: show daily
			return 'day';
		} else if (diffDays <= 730) {
			// Up to 2 years: show monthly
			return 'month';
		} else {
			// More than 2 years: show yearly
			return 'year';
		}
	};

	// Function to aggregate data based on period
	const aggregateDataByPeriod = (data: SalesDataItem[]) => {
		if (data.length === 0) {
			setAggregatedData([]);
			setCategories([]);
			return;
		}

		let aggregationType: 'day' | 'month' | 'year';

		// Determine aggregation type based on period or date range
		if (selectedPeriod === 'This year') {
			aggregationType = 'month';
		} else if (selectedPeriod === 'This week') {
			aggregationType = 'day';
		} else if (selectedPeriod === 'This month') {
			aggregationType = 'day';
		} else {
			// Custom Range - intelligently choose aggregation
			aggregationType = determineAggregationType(startDate, endDate);
		}

		if (aggregationType === 'year') {
			// Aggregate by year
			const yearlyData: Record<string, number> = {};
			const yearOrder: string[] = [];

			data.forEach((item) => {
				const date = new Date(item.date);
				const year = date.getFullYear().toString();
				if (!yearlyData[year]) {
					yearlyData[year] = 0;
					yearOrder.push(year);
				}
				yearlyData[year] += parseFloat(item.total_amount_sold);
			});

			yearOrder.sort();
			const aggregated = yearOrder.map((year) => ({
				date: year,
				value: yearlyData[year],
			}));

			setAggregatedData(aggregated);
			setCategories(yearOrder);
		} else if (aggregationType === 'month') {
			// Aggregate by month
			const monthlyData: Record<string, number> = {};
			const monthOrder: string[] = [];

			if (selectedPeriod === 'This year') {
				// For "This year", show only elapsed months
				const today = new Date();
				const currentMonth = today.getMonth(); // 0-indexed

				for (let i = 0; i <= currentMonth; i++) {
					const monthName = new Date(2000, i, 1).toLocaleString('default', { month: 'long' });
					monthlyData[monthName] = 0;
					monthOrder.push(monthName);
				}

				data.forEach((item) => {
					const date = new Date(item.date);
					const monthName = date.toLocaleString('default', { month: 'long' });
					if (monthlyData.hasOwnProperty(monthName)) {
						monthlyData[monthName] += parseFloat(item.total_amount_sold);
					}
				});
			} else {
				// For custom range, show month-year format
				data.forEach((item) => {
					const date = new Date(item.date);
					const monthYear = date.toLocaleString('default', { month: 'short', year: 'numeric' });
					if (!monthlyData[monthYear]) {
						monthlyData[monthYear] = 0;
						monthOrder.push(monthYear);
					}
					monthlyData[monthYear] += parseFloat(item.total_amount_sold);
				});
			}

			const aggregated = monthOrder.map((month) => ({
				date: month,
				value: monthlyData[month],
			}));

			setAggregatedData(aggregated);
			setCategories(monthOrder);
		} else {
			// Daily aggregation
			if (selectedPeriod === 'This week') {
				// Aggregate by day of week for "This week"
				const weekdayData: Record<string, number> = {};
				const daysOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
				const today = new Date();
				const currentDayIndex = today.getDay() === 0 ? 6 : today.getDay() - 1; // Convert Sunday=0 to Monday=0 indexing

				// Only include days up to today
				const includedDays = daysOrder.slice(0, currentDayIndex + 1);

				includedDays.forEach((day) => {
					weekdayData[day] = 0;
				});

				data.forEach((item) => {
					const date = new Date(item.date);
					const dayName = date.toLocaleString('default', { weekday: 'long' });
					if (weekdayData.hasOwnProperty(dayName)) {
						weekdayData[dayName] += parseFloat(item.total_amount_sold);
					}
				});

				const aggregated = includedDays.map((day) => ({
					date: day,
					value: weekdayData[day],
				}));

				setAggregatedData(aggregated);
				setCategories(includedDays);
			} else if (selectedPeriod === 'This month') {
				// For "This month", only show days that have elapsed
				const today = new Date();
				const dailyData: Record<string, number> = {};
				const dayOrder: string[] = [];

				// Create entries for each day of the month up to today
				for (let day = 1; day <= today.getDate(); day++) {
					const dateStr = `${day}${
						day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th'
					} ${today.toLocaleString('default', { month: 'short' })}`;
					dailyData[dateStr] = 0;
					dayOrder.push(dateStr);
				}

				data.forEach((item) => {
					const date = new Date(item.date);
					// Only include data from the current month and up to today
					if (
						date.getMonth() === today.getMonth() &&
						date.getFullYear() === today.getFullYear() &&
						date.getDate() <= today.getDate()
					) {
						const dateStr = formatDateForPeriod(date);
						if (dailyData.hasOwnProperty(dateStr)) {
							dailyData[dateStr] += parseFloat(item.total_amount_sold);
						}
					}
				});

				const aggregated = dayOrder.map((day) => ({
					date: day,
					value: dailyData[day],
				}));

				setAggregatedData(aggregated);
				setCategories(dayOrder);
			} else {
				// For other daily views, show date
				const formatted = data.map((item) => ({
					date: formatDateForPeriod(new Date(item.date)),
					value: parseFloat(item.total_amount_sold),
				}));

				setAggregatedData(formatted);
				setCategories(formatted.map((item) => item.date));
			}
		}
	};

	useEffect(() => {
		setStartDate(initialStartDate);
		setEndDate(initialEndDate);
	}, [initialStartDate, initialEndDate]);

	useEffect(() => {
		filterSalesData();
	}, [startDate, endDate, salesData, selectedPeriod]);

	const filterSalesData = () => {
		setIsLoading(true);

		// Filter the provided salesData based on the date range
		const filtered = salesData.filter((item) => {
			const itemDate = new Date(item.date);
			return itemDate >= startDate && itemDate <= endDate;
		});

		setFilteredSalesData(filtered);
		aggregateDataByPeriod(filtered);
		setIsLoading(false);
	};

	const isMobile = window.innerWidth <= 768;

	const options = {
		chart: {
			id: 'sales-area',
			type: 'area',
			toolbar: {
				show: false,
			},
			zoom: {
				enabled: !isMobile,
			},
			fontFamily: 'inherit',
			animations: {
				enabled: true,
				easing: 'easeinout',
				speed: 800,
				animateGradually: {
					enabled: true,
					delay: 150,
				},
				dynamicAnimation: {
					enabled: true,
					speed: 350,
				},
			},
		},
		colors: ['#4299e1'],
		fill: {
			type: 'gradient',
			gradient: {
				shadeIntensity: 1,
				opacityFrom: 0.7,
				opacityTo: 0.1,
				stops: [0, 90, 100],
			},
		},
		stroke: {
			curve: 'smooth',
			width: 3,
		},
		dataLabels: {
			enabled: false,
		},
		markers: {
			size: 5,
			colors: ['#4299e1'],
			strokeColors: '#fff',
			strokeWidth: 2,
			hover: {
				size: 7,
			},
		},
		grid: {
			show: true,
			borderColor: '#f7fafc',
			strokeDashArray: 5,
			position: 'back',
			xaxis: {
				lines: {
					show: false,
				},
			},
			yaxis: {
				lines: {
					show: true,
				},
			},
			padding: {
				top: 0,
				right: 0,
				bottom: 0,
				left: 10,
			},
		},
		xaxis: {
			categories: categories,
			labels: {
				style: {
					fontSize: '12px',
					colors: '#8795a1',
				},
			},
			axisBorder: {
				show: false,
			},
			axisTicks: {
				show: false,
			},
			crosshairs: {
				show: true,
				position: 'back',
				stroke: {
					color: '#b8c2cc',
					width: 1,
					dashArray: 3,
				},
			},
		},
		yaxis: {
			min: 0,
			forceNiceScale: true,
			labels: {
				formatter: function (value: number) {
					if (value >= 1000) {
						return `${Math.round(value / 1000)}k`;
					}
					return value.toString();
				},
				style: {
					fontSize: '12px',
					colors: '#8795a1',
				},
			},
			axisBorder: {
				show: false,
			},
			axisTicks: {
				show: false,
			},
		},
		tooltip: {
			y: {
				formatter: function (value: number) {
					return currency + value.toLocaleString();
				},
			},
			x: {
				formatter: function (value: string, ctx: TooltipContext) {
					// Return the category name/label directly
					return ctx.w.globals.categoryLabels[ctx.dataPointIndex];
				},
			},
			custom: function (ctx: CustomTooltipContext) {
				const value = ctx.series[ctx.seriesIndex][ctx.dataPointIndex];

				return `<div class="apexcharts-tooltip-box">
                    <span style="color: #4299e1; margin-right: 5px;">●</span>
                    <span>Sales: </span>
                    <span style="font-weight: 600;">${currency}${value.toLocaleString()}</span>
                </div>`;
			},
		},
		noData: {
			text: isLoading ? 'Loading sales data...' : 'No sales data for selected period',
			align: 'center',
			verticalAlign: 'middle',
			offsetX: 0,
			offsetY: 0,
			style: {
				color: '#888',
				fontSize: '14px',
				fontFamily: 'inherit',
			},
		},
	};

	const series = [
		{
			name: 'Sales',
			data: aggregatedData.map((item) => item.value),
		},
	];

	// Calculate total sales
	const totalSales = filteredSalesData.reduce((sum, item) => sum + parseFloat(item.total_amount_sold), 0);

	return (
		<ChartContainer>
			<ChartHeader>
				<Title>Sales</Title>
				<Subtitle>Breakdown of your sales.</Subtitle>

				<MetricsContainer>
					<MetricBox>
						<MetricLabel style={{ color: '#5749A8' }}>Total Sales</MetricLabel>
						<MetricValue>
							<CurrencyPrefix>{currency}</CurrencyPrefix>
							{totalSales.toLocaleString(undefined, {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
							})}
						</MetricValue>
					</MetricBox>
				</MetricsContainer>
			</ChartHeader>

			<div>
				{isLoading ? (
					<div style={{ height: '400px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
						Loading sales data...
					</div>
				) : (
					<ReactApexChart options={options as any} series={series} type="area" height={400} />
				)}
			</div>
		</ChartContainer>
	);
};

export default SalesChart;
